import {
  DataGrid,
  FormField,
  FormInputDate,
  FormSelect,
  GridColDef,
  TextArea,
} from '@fashionnovateam/nova-ui-toolkit';
import { useMemo } from 'react';
import classNames from 'classnames';
import { formatDate } from '../utils';
import { PurchaseOrder } from '../../../Lib/Types/purchase-order';
import { FormErrors, PurchaseOrderRevisionForm } from './validation';
import { useGetMetaData } from '../../../Lib/Hooks/MetadataService';
import { fnHolidays } from './fnHolidays';
import { ExcludeSpecialCharacters } from '../../../Lib/Constants/regexes';

type DueDateFormProps = {
  purchaseOrder: PurchaseOrder;
  form: NonNullable<PurchaseOrderRevisionForm['due_date']>;
  updateForm: (
    input: NonNullable<PurchaseOrderRevisionForm['due_date']>,
  ) => void;
  disabled: boolean;
  errors: FormErrors | undefined;
};

const columns: GridColDef[] = [
  {
    field: 'header',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date_diff',
    headerName: 'Day Diff',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

const columnsFCA: GridColDef[] = [
  {
    field: 'header',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'fob_ship_date',
    headerName: 'FOB Ship Date',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date_diff',
    headerName: 'Day Diff',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

function getDueDateDiff(dueDate: string, originalDueDate: string) {
  const dueDateDate = new Date(dueDate);
  const originalDueDateDate = new Date(originalDueDate);

  const diffTime =
    Date.UTC(
      dueDateDate.getUTCFullYear(),
      dueDateDate.getUTCMonth(),
      dueDateDate.getUTCDate(),
    ) -
    Date.UTC(
      originalDueDateDate.getUTCFullYear(),
      originalDueDateDate.getUTCMonth(),
      originalDueDateDate.getUTCDate(),
    );

  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

function addDaysToDate(dateStr: string, daysToAdd: number) {
  // Parse YYYY-MM-DD explicitly in UTC to avoid timezone issues
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  // Add the days in UTC
  date.setUTCDate(date.getUTCDate() + daysToAdd);

  return date.toISOString().split('T')[0];
}

export function DueDateForm(props: DueDateFormProps) {
  const {
    purchaseOrder: {
      incoterm,
      fob_ship_date: currentFobShipDate,
      original_fob_ship_date: originalFobShipDate,
      due_date: currentDueDate,
      original_due_date: originalDueDate,
    },
    form,
    updateForm,
    disabled,
    errors,
  } = props;

  const isFca = incoterm === 'FCA';

  const reasonsProcess = useGetMetaData<Array<string>>('po_revision_reasons');

  const transformedRows = useMemo(
    () => [
      {
        id: 1,
        header: <span className="text-sm text-base-disabled">Requested</span>,
        fob_ship_date: (
          <span className="text-sm font-semibold text-warning-700">
            {form.new_fob_ship_date_value
              ? formatDate(form.new_fob_ship_date_value, 'numeric', 'UTC')
              : '-'}
          </span>
        ),
        due_date: (
          <span className="text-sm font-semibold text-warning-700">
            {form.new_value
              ? formatDate(form.new_value, 'numeric', 'UTC')
              : '-'}
          </span>
        ),
        due_date_diff: (
          <span className="text-sm text-base-primary">
            {(!isFca &&
              form.new_value &&
              (originalDueDate || currentDueDate)) ||
            (isFca &&
              form.new_fob_ship_date_value &&
              (originalFobShipDate || currentFobShipDate))
              ? getDueDateDiff(
                  (isFca ? form.new_fob_ship_date_value : form.new_value) || '',
                  (isFca
                    ? originalFobShipDate || currentFobShipDate
                    : originalDueDate || currentDueDate) || '',
                )
              : '-'}
          </span>
        ),
      },
      {
        id: 2,
        header: <span className="text-sm text-base-disabled">Currently</span>,
        fob_ship_date: (
          <span
            className={classNames(
              'text-sm',
              form.new_fob_ship_date_value && currentFobShipDate
                ? 'text-base-disabled line-through decoration-base-disabled'
                : '',
            )}
          >
            {currentFobShipDate
              ? formatDate(currentFobShipDate, 'numeric', 'UTC')
              : '-'}
          </span>
        ),
        due_date: (
          <span
            className={classNames(
              'text-sm',
              form.new_value && currentDueDate
                ? 'text-base-disabled line-through decoration-base-disabled'
                : '',
            )}
          >
            {currentDueDate
              ? formatDate(currentDueDate, 'numeric', 'UTC')
              : '-'}
          </span>
        ),
        due_date_diff: (
          <span className="text-sm text-base-primary">
            {(!isFca && currentDueDate && originalDueDate) ||
            (isFca && currentFobShipDate && originalFobShipDate)
              ? getDueDateDiff(
                  (isFca ? currentFobShipDate : currentDueDate) || '',
                  (isFca ? originalFobShipDate : originalDueDate) || '',
                ) || '-'
              : '-'}
          </span>
        ),
      },
      ...((!isFca && originalDueDate) || (isFca && originalFobShipDate)
        ? [
            {
              id: 3,
              header: (
                <span className="text-sm text-base-disabled">Original</span>
              ),
              fob_ship_date: (
                <span className="text-sm text-base-disabled">
                  {originalFobShipDate
                    ? formatDate(originalFobShipDate, 'numeric', 'UTC')
                    : '-'}
                </span>
              ),
              due_date: (
                <span className="text-sm text-base-disabled">
                  {originalDueDate
                    ? formatDate(originalDueDate, 'numeric', 'UTC')
                    : '-'}
                </span>
              ),
              due_date_diff: (
                <span className="text-sm text-base-primary">-</span>
              ),
            },
          ]
        : []),
    ],
    [
      currentDueDate,
      originalDueDate,
      currentFobShipDate,
      originalFobShipDate,
      form.new_value,
      form.new_fob_ship_date_value,
      isFca,
    ],
  );

  // Early shipping rule: due date is today + 2 days
  const notBefore = useMemo(() => {
    const date = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    return date.toISOString().split('T')[0];
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <FormInputDate
          label={isFca ? 'Requested FOB Ship Date' : 'Requested Due Date'}
          value={isFca ? form.new_fob_ship_date_value : form.new_value}
          onChangeValue={(v) =>
            updateForm({
              ...form,
              ...(isFca
                ? {
                    new_fob_ship_date_value: v,
                    new_value: addDaysToDate(v, 14),
                  }
                : {
                    new_value: v,
                  }),
              vendor_acknowledged:
                new Date(isFca ? addDaysToDate(v, 14) : v) >
                new Date(originalDueDate || currentDueDate)
                  ? false
                  : undefined,
            })
          }
          required
          disabled={disabled}
          error={
            errors?.['due_date.new_fob_ship_date_value'] ||
            errors?.['due_date.new_value']
          }
          placeholder="Add"
          disabledWeekDays={['Saturday', 'Sunday']}
          notBefore={notBefore}
          disabledDays={isFca ? undefined : fnHolidays}
        />
      </div>

      <DataGrid
        hideFilterButton
        hideGroupButton
        hideFooterPagination
        hideFooter
        hideSortButton
        rowHeight={32}
        rows={transformedRows}
        columns={isFca ? columnsFCA : columns}
      />

      {(isFca ? form.new_fob_ship_date_value : form.new_value) && (
        <FormSelect
          label="Reason"
          options={
            reasonsProcess.data?.value.map((reason) => ({
              value: reason,
              label: reason,
            })) || []
          }
          placeholder="Select ..."
          value={form.late_reason ?? ''}
          onChangeValue={(v) =>
            updateForm({
              ...form,
              late_reason: v || undefined,
              note: v.startsWith('OTR') ? form.note || '' : undefined,
            })
          }
          required
          disabled={disabled}
          error={errors?.['due_date.late_reason']}
        />
      )}

      {form.late_reason?.startsWith('OTR') && (
        <FormField
          label="Reason Note"
          required
          error={errors?.['due_date.note']}
        >
          <TextArea
            value={form.note || ''}
            onChange={(e) =>
              updateForm({
                ...form,
                note:
                  e.target.value.replace(ExcludeSpecialCharacters, '') ||
                  undefined,
              })
            }
            required
            disabled={disabled}
            error={!!errors?.['due_date.note']}
          />
        </FormField>
      )}
    </div>
  );
}
